import * as React from 'react';
import classNames from 'clsx';
import {
  convertDateRangesArray,
  convertDateStringToDate,
  DatePickerDate,
  findCurrentRange,
  findFutureRange,
  findPastRange,
  getDisabledReasons,
  getMonthBoundaries,
  offsetDateByDays,
} from '@wix/editor-elements-corvid-utils';
import { Spinner } from 'wix-ui-tpa/cssVars';
import {
  formatClassNames,
  getDataAttributes,
  keyCodes,
  useDomDirection,
} from '@wix/editor-elements-common-utils';
import type {
  ICalendarMonthImperativeActions,
  IDatePickerCalendarProps,
} from '../DatePickerCalendar.types';
import { useClickOutside } from '../../../providers/useClickOutside/useClickOutside';
import semanticClassNames from '../../DatePicker/WIP_DatePicker.semanticClassNames';
import style from './styles/DatePickerCalendar.scss';
import Month from './components/Month';
import Years from './components/Years';
import CalendarError from './components/CalendarError';
import { WithTabbingTrap } from '../../../providers/WithTabbingTrap';

const noop = () => {};

const Calendar: React.FC<IDatePickerCalendarProps> = props => {
  const {
    id,
    skin,
    value,
    minDate,
    maxDate,
    allowPastDates,
    allowFutureDates,
    disabledDates,
    enabledDateRanges,
    disabledDateRanges,
    disabledDaysOfWeek,
    weekStartDay,
    isCompactMode,
    inputWrapperRef,
    className,
    onApply = noop,
    onCancel = noop,
    onClick = noop,
    onDblClick = noop,
    NavbarComponent,
    onMouseEnter = noop,
    onMouseLeave = noop,
    isResponsive,
    timeZone,
    translations,
    popperRef,
    popperAttributes,
    popperStyles,
    onViewChange,
    calendarLoading,
    calendarError,
  } = props;
  const yearButtonRef = React.useRef<HTMLButtonElement>(null);
  const monthRef = React.useRef<ICalendarMonthImperativeActions>(null);
  const { direction: domDirection, directionRef: calendarRef } =
    useDomDirection<HTMLDivElement>();

  React.useEffect(() => {
    monthRef.current?.focus();
  }, [monthRef]);

  const onClickOutside = React.useCallback(() => {
    onCancel({ focusInput: false });
  }, [onCancel]);

  useClickOutside([calendarRef, inputWrapperRef], onClickOutside, true);

  const [isYearsMode, setYearsMode] = React.useState<boolean>(false);
  const [shouldFocusSelectedYear, setShouldFocusSelectedYear] =
    React.useState<boolean>(false);
  const onCurrentYearClick = () => {
    setYearsMode(!isYearsMode);

    if (!isYearsMode) {
      setShouldFocusSelectedYear(true);
    }
  };

  const todayDate = React.useMemo<Date>(
    () =>
      new DatePickerDate({
        type: 'Now',
        timeZone: timeZone || 'Local',
      }).getAsDate('Local'),
    [timeZone],
  );

  const findNearestValidDate = React.useCallback(
    ({
      date: startingDate,
      direction = 'FUTURE',
    }: {
      date: Date;
      direction?: 'FUTURE' | 'PAST';
    }): Date | undefined => {
      const _enabledDateRanges = enabledDateRanges
        ? convertDateRangesArray(enabledDateRanges, convertDateStringToDate)
        : null;
      const _disabledDateRanges = disabledDateRanges
        ? convertDateRangesArray(disabledDateRanges, convertDateStringToDate)
        : null;
      const _minDate = minDate ? new Date(minDate) : undefined;
      const _maxDate = maxDate ? new Date(maxDate) : undefined;
      let date: Date | undefined = startingDate;
      while (date) {
        const {
          isDisabledByMaxDate,
          isDisabledByMinDate,
          isDisabledByAllowFutureDates,
          isDisabledByAllowPastDates,
          isDisabledByDisabledDaysOfWeek,
          isDisabledByDisabledDates,
          isDisabledByDisabledDateRanges,
          isDisabledByEnabledDateRanges,
        } = getDisabledReasons({
          date,
          todayDate,
          minDate: _minDate,
          maxDate: _maxDate,
          allowPastDates,
          allowFutureDates,
          disabledDaysOfWeek,
          disabledDates: disabledDates.map(d => new Date(d)),
          enabledDateRanges: _enabledDateRanges,
          disabledDateRanges: _disabledDateRanges,
        });
        // Check ranges that extend to infinity
        if (isDisabledByMaxDate) {
          return direction === 'FUTURE' ? undefined : _maxDate;
        }
        if (isDisabledByMinDate) {
          return direction === 'PAST' ? undefined : _minDate;
        }
        if (isDisabledByAllowFutureDates) {
          return direction === 'FUTURE' ? undefined : todayDate;
        }
        if (isDisabledByAllowPastDates) {
          return direction === 'PAST' ? undefined : todayDate;
        }
        // Check if disabled by single dates
        if (isDisabledByDisabledDaysOfWeek || isDisabledByDisabledDates) {
          date = offsetDateByDays(date, direction === 'FUTURE' ? 1 : -1);
          continue;
        }

        if (isDisabledByDisabledDateRanges) {
          const currentRange = findCurrentRange({
            date,
            availableRanges: _disabledDateRanges,
          });
          date =
            direction === 'FUTURE'
              ? currentRange?.endDate &&
                offsetDateByDays(currentRange.endDate, 1)
              : currentRange?.startDate &&
                offsetDateByDays(currentRange.startDate, -1);
          continue;
        }

        if (isDisabledByEnabledDateRanges) {
          date =
            direction === 'FUTURE'
              ? findFutureRange({ date, availableRanges: _enabledDateRanges })
                  ?.startDate
              : findPastRange({ date, availableRanges: _enabledDateRanges })
                  ?.endDate;
          continue;
        }
        return date;
      }
      return undefined;
    },
    [
      enabledDateRanges,
      disabledDateRanges,
      disabledDaysOfWeek,
      disabledDates,
      allowPastDates,
      allowFutureDates,
      minDate,
      maxDate,
      todayDate,
    ],
  );

  const [focusedDate, setFocusedDate] = React.useState<Date>(
    value ?? todayDate,
  );
  const [year, setYear] = React.useState<number>(2000);
  const [month, setMonth] = React.useState<number>(0);

  React.useEffect(() => {
    const dateForMonthAndYear = value || todayDate;
    setYear(dateForMonthAndYear.getFullYear());
    setMonth(dateForMonthAndYear.getMonth());
  }, [value, timeZone, todayDate]);

  const changeFocusedDate = (_year: number, _month: number, _day: number) => {
    const daysInMonth = new Date(year, _month + 1, 0).getDate();
    return setFocusedDate(
      new Date(_year, _month, _day > daysInMonth ? daysInMonth : _day),
    );
  };

  const onMonthChange = (_month: number) => {
    if (_month < 0) {
      setYear(year - 1);
      setMonth(11);
      changeFocusedDate(year - 1, 11, focusedDate.getDate());
    } else if (_month > 11) {
      setYear(year + 1);
      setMonth(0);
      changeFocusedDate(year + 1, 0, focusedDate.getDate());
    } else {
      setMonth(_month);
      changeFocusedDate(year, _month, focusedDate.getDate());
    }
  };

  const onDayChange = (_day: number) => {
    onApply(new Date(year, month, _day));
  };

  const onYearChange = ({
    selectedYear,
    exitYearsMode,
  }: {
    selectedYear: number;
    exitYearsMode: boolean;
  }) => {
    const updatedValue = value ? new Date(value) : new Date();
    updatedValue.setFullYear(selectedYear);

    changeFocusedDate(selectedYear, month, focusedDate.getDate());
    if (exitYearsMode) {
      yearButtonRef.current?.focus();
      changeYearFromYearsMode(selectedYear);
    } else {
      setYear(selectedYear);
    }
  };

  const changeYearFromYearsMode = (_year: number) => {
    setYear(_year);
    setYearsMode(false);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = event => {
    if (event.keyCode === keyCodes.escape) {
      onCancel({ focusInput: true });
      event.stopPropagation();
    }
  };

  const containerClasses = classNames(style.calendarWrapper, className, {
    [style.responsive]: isResponsive,
  });

  const calendarHeight = React.useMemo(
    () => (isCompactMode ? '100%' : 'auto'),
    [isCompactMode],
  );

  const styles: React.CSSProperties = React.useMemo(() => {
    const calendarHeightKey: string = '--calendarHeight';

    const cssStyles = {
      [calendarHeightKey]: calendarHeight,
    };

    return cssStyles;
  }, [calendarHeight]);

  // Using layout effect since we want to call
  // onViewChange as soon as possible, its not a
  // heavy operation so its ok
  React.useLayoutEffect(() => {
    const { startDate, endDate } = getMonthBoundaries({
      year,
      month: month + 1,
      timeZone: timeZone || 'Local',
    });

    onViewChange({
      type: 'onViewChange',
      compId: id,
      options: { startDate, endDate },
    });
  }, [onViewChange, year, month, timeZone, id]);

  const handleOnKeyDown = (event: React.KeyboardEvent) => {
    const offset = getDaysOffset(event);
    if (offset !== 0) {
      event.preventDefault();
      const date = findNearestValidDate({
        direction: offset > 0 ? 'FUTURE' : 'PAST',
        date: offsetDateByDays(focusedDate, offset),
      });
      if (date) {
        setFocusedDate(date);
        if (date.getMonth() !== month) {
          setMonth(date.getMonth());
        }
        if (date.getFullYear() !== year) {
          setYear(date.getFullYear());
        }
      }
    }
  };

  const getDaysOffset = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowUp':
        return -7;
      case 'ArrowDown':
        return 7;
      case 'ArrowLeft':
        return domDirection === 'rtl' ? 1 : -1;
      case 'ArrowRight':
        return domDirection === 'rtl' ? -1 : 1;
      default:
        return 0;
    }
  };

  return (
    <WithTabbingTrap>
      <div
        id={id}
        {...getDataAttributes(props)}
        data-testid="calendar"
        className={containerClasses}
        onClick={onClick}
        onDoubleClick={onDblClick}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{ ...styles, ...popperStyles }}
        {...popperAttributes}
        ref={popperRef}
      >
        <div className={style.beforeCalendar} />
        <div
          ref={calendarRef}
          className={classNames(
            style.calendar,
            formatClassNames(semanticClassNames.calendar),
          )}
        >
          {calendarLoading && (
            <Spinner className={style.calendarSpinner} isCentered />
          )}
          <NavbarComponent
            skin={skin}
            isCompactMode={isCompactMode}
            year={year}
            month={month}
            onYearChange={_year =>
              onYearChange({ selectedYear: _year, exitYearsMode: false })
            }
            onMonthChange={onMonthChange}
            onCurrentYearClick={onCurrentYearClick}
            yearButtonRef={yearButtonRef}
            translations={translations}
          />
          <div
            className={classNames(style.belowNavBarContent, {
              [style.loadingMode]: calendarLoading,
            })}
          >
            {calendarError && !isYearsMode ? (
              <CalendarError
                calendarError={calendarError}
                translations={translations}
              />
            ) : (
              <>
                {isYearsMode || (
                  <Month
                    ref={monthRef}
                    translations={translations}
                    value={value}
                    minDate={minDate}
                    maxDate={maxDate}
                    allowPastDates={allowPastDates}
                    allowFutureDates={allowFutureDates}
                    disabledDates={disabledDates}
                    enabledDateRanges={enabledDateRanges}
                    disabledDateRanges={disabledDateRanges}
                    disabledDaysOfWeek={disabledDaysOfWeek}
                    weekStartDay={weekStartDay}
                    year={year}
                    month={month}
                    onDayChange={onDayChange}
                    timeZone={timeZone}
                    isLoadingMode={calendarLoading}
                    focusedDate={focusedDate}
                    onKeyDown={handleOnKeyDown}
                  />
                )}
                {isYearsMode && (
                  <Years
                    year={year}
                    onYearChange={onYearChange}
                    shouldFocusSelectedYear={shouldFocusSelectedYear}
                    exitYearsMode={() => {
                      yearButtonRef.current?.focus();
                      setYearsMode(false);
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </WithTabbingTrap>
  );
};

export default Calendar;
